/**
 * 轻量 Toast（FE-E3b 注入式下沉）：基于 context + 自渲染队列的 card 风格
 * （右上角卡片：图标 + 文案 + 关闭按钮），渲染骨架与 admin/merchant/customer
 * 原实现逐字一致；端差异全部经 ToastProviderProps 注入（缺省= merchant/customer
 * 现值，admin 壳注入其现值），见 FE-E3b 报告差异表。
 *
 * keep：agent 端为纯色块风格（bottom-right 无图标、3000ms、z-50），渲染范式不同，
 * 不并入（本模块仅 card 变体）。
 *
 * 本模块不 import 任何门户代码（verify-p2-boundary）。
 */
import { createContext, useCallback, useContext, useMemo, useState } from 'react';
import type { ReactNode } from 'react';
import { CheckCircle2, AlertCircle, Info, X } from 'lucide-react';

export type ToastKind = 'success' | 'error' | 'info';

interface ToastItem {
  id: number;
  kind: ToastKind;
  text: string;
}

export interface ToastApi {
  push: (kind: ToastKind, text: string) => void;
  success: (text: string) => void;
  error: (text: string) => void;
  info: (text: string) => void;
}

export interface ToastProviderProps {
  children: ReactNode;
  /** 各 kind 停留时长（ms）；缺省统一 3200（merchant/customer 现值；admin 注入 error:7000） */
  durations?: { success?: number; error?: number; info?: number };
  /** 容器 z-index class；缺省 'z-[100]'（merchant/customer 现值；admin 注入 'z-[200]'） */
  zIndexClass?: string;
  /** 单条最大宽度 class；缺省 'max-w-[400px]'（merchant/customer 现值；admin 注入 'max-w-[440px]'） */
  maxWidthClass?: string;
  /** 单条附加 class（如 'pointer-events-auto shadow-lg'，admin 现值）；缺省无 */
  itemExtraClass?: string;
  /** error 卡片强化样式 class（admin 现值：红边浅红底）；缺省关闭 */
  errorItemClass?: string;
  /** error 卡片是否带 role=alert / 其余 status（admin 现值）；缺省统一 status */
  errorRoleAlert?: boolean;
}

const ToastContext = createContext<ToastApi | null>(null);

let seq = 0;

export function ToastProvider({
  children,
  durations,
  zIndexClass = 'z-[100]',
  maxWidthClass = 'max-w-[400px]',
  itemExtraClass = '',
  errorItemClass = '',
  errorRoleAlert = false,
}: ToastProviderProps) {
  const [items, setItems] = useState<ToastItem[]>([]);

  const remove = useCallback((id: number) => {
    setItems((prev) => prev.filter((t) => t.id !== id));
  }, []);

  const push = useCallback(
    (kind: ToastKind, text: string) => {
      const id = ++seq;
      setItems((prev) => [...prev, { id, kind, text }]);
      const fallback = 3200;
      const durationMs = durations?.[kind] ?? fallback;
      setTimeout(() => remove(id), durationMs);
    },
    [remove, durations],
  );

  const api = useMemo<ToastApi>(
    () => ({
      push,
      success: (t) => push('success', t),
      error: (t) => push('error', t),
      info: (t) => push('info', t),
    }),
    [push],
  );

  const icons = {
    success: <CheckCircle2 className="w-5 h-5 text-emerald-500" />,
    error: <AlertCircle className="w-5 h-5 text-red-500" />,
    info: <Info className="w-5 h-5 text-brand-500" />,
  };

  return (
    <ToastContext.Provider value={api}>
      {children}
      {/* items 为空时不渲染 fixed 容器（避免空容器覆盖右上操作区/CTA）；容器 pointer-events-none 防拦截下层点击 */}
      {items.length > 0 && (
        <div className={`fixed top-4 right-4 ${zIndexClass} flex flex-col gap-2 pointer-events-none`}>
          {items.map((t) => (
            <div
              key={t.id}
              className={[
                'card flex items-start gap-2.5 px-4 py-3 min-w-[260px]',
                maxWidthClass,
                'animate-in',
                itemExtraClass,
                t.kind === 'error' ? errorItemClass : '',
              ].filter(Boolean).join(' ')}
              role={errorRoleAlert && t.kind === 'error' ? 'alert' : 'status'}
            >
              {icons[t.kind]}
              <span className="text-sm text-slate-700 flex-1 leading-relaxed">{t.text}</span>
              <button
                className="text-slate-400 hover:text-slate-600 pointer-events-auto"
                onClick={() => remove(t.id)}
                aria-label="关闭提示"
              >
                <X className="w-4 h-4" />
              </button>
            </div>
          ))}
        </div>
      )}
    </ToastContext.Provider>
  );
}

export function useToast(): ToastApi {
  const ctx = useContext(ToastContext);
  if (!ctx) throw new Error('useToast 必须在 ToastProvider 内使用');
  return ctx;
}
